import React, {
  FC,
  forwardRef,
  FunctionComponentElement,
  ReactNode,
} from 'react';

import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  iconButtonClasses,
  InputAdornment,
  inputAdornmentClasses,
  svgIconClasses,
  SvgIconProps,
} from '@mui/material';
import { formHelperTextClasses } from '@mui/material/FormHelperText';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';

import { IIconButtonProps } from './IconButton';

const getOutlinedStyles = (theme: Theme, multiline?: boolean) => css`
  legend {
    display: none;
  }

  .${inputLabelClasses.outlined} {
    top: -2px;
  }

  .${inputLabelClasses.outlined}.${inputLabelClasses.shrink} {
    top: 0;
  }

  .${outlinedInputClasses.input} {
    position: relative;
    padding: 0;
  }

  .${outlinedInputClasses.input} {
    &:-webkit-autofill {
      padding: 0;
      border-radius: 0;
    }
  }

  .${outlinedInputClasses.root} {
    height: ${multiline ? 'fit-content' : theme.size['3000']};
    border-radius: 8px;
    padding: ${theme.spacing('1000')};
    align-items: ${multiline ? 'initial' : 'center'};

    &:hover {
      .${outlinedInputClasses.notchedOutline} {
        box-shadow: ${theme.textField.boxShadow.hover};
        border-color: ${theme.color.outlineVariant};
      }
    }
  }

  &
    .${outlinedInputClasses.notchedOutline},
    .${outlinedInputClasses.root}.${outlinedInputClasses.disabled}
    .${outlinedInputClasses.notchedOutline} {
    inset: 0;
    border-color: ${theme.color.outlineVariant};
  }

  &
    .${outlinedInputClasses.root}.${outlinedInputClasses.focused}
    .${outlinedInputClasses.notchedOutline} {
    border: 1px solid ${theme.color.outlineVariant};
    box-shadow: ${theme.textField.boxShadow.focused};
  }

  .${outlinedInputClasses.root}.${outlinedInputClasses.disabled} {
    opacity: 0.4;
  }

  .${outlinedInputClasses.input}.${outlinedInputClasses.disabled} {
    -webkit-text-fill-color: ${theme.color.outlineVariant};
  }
`;

const StyledTextField = styled(MuiTextField)<{
  multiline: ITextFieldProps['multiline'];
}>`
  .${inputLabelClasses.root} {
    color: ${({ theme }) => theme.color.outlineVariant};
  }

  .${inputLabelClasses.root}.${inputLabelClasses.focused} {
    color: ${({ theme }) => theme.color.outlineVariant};
  }

  .${inputLabelClasses.root}.${inputLabelClasses.error},
    .${inputLabelClasses.root}.${inputLabelClasses.disabled}.${inputLabelClasses.error},
    .${inputLabelClasses.root}.${inputLabelClasses.error}.${inputLabelClasses.focused},
    .${formHelperTextClasses.root}.${formHelperTextClasses.error},
    .${formHelperTextClasses.root}.${formHelperTextClasses.disabled}.${formHelperTextClasses.error} {
    color: ${({ theme }) => theme.color.redFixed};
  }

  .${inputBaseClasses.input} {
    font: ${({ theme }) => theme.typography.body.regular};
    font-size: ${({ theme }) => theme.typescale.size['1']};
    line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
    color: ${({ theme }) => theme.textField.enabled.inputText.fill};
    text-overflow: ellipsis;
  }

  .${formHelperTextClasses.root} {
    margin: 0;
    padding-block: 4px;
    color: ${({ theme }) => theme.color.outlineVariant};
  }

  .${inputLabelClasses.root}.${outlinedInputClasses.disabled} {
    color: ${({ theme }) => theme.color.outlineVariant};
  }

  .${formHelperTextClasses.root}.${outlinedInputClasses.disabled} {
    color: ${({ theme }) => theme.color.outlineVariant};
  }

  .${formHelperTextClasses.root}.${formHelperTextClasses.error} {
    font: ${({ theme }) => theme.typography.body.regular};
    font-size: ${({ theme }) => theme.typescale.size['0875']};
    text-align: right;
  }

  .${inputAdornmentClasses.root} {
    height: 100%;
    gap: 4px;
    color: ${({ theme }) => theme.textField.enabled.inputText.fill};
    position: relative;
    margin-inline: 0;

    .notranslate {
      display: none;
    }
  }

  ${({ theme, multiline }) => getOutlinedStyles(theme, multiline)};
`;

const AdornmentContainer = styled.div<{
  position: 'start' | 'end';
}>`
  ${({ position, theme }) =>
    css`
  
  .${svgIconClasses.root} {
    margin-${position === 'start' ? 'left' : 'right'}: 0;
    margin-${position === 'start' ? 'right' : 'left'}: ${theme.size['1000']};
  }

  .${iconButtonClasses.root} {
    margin-${position === 'start' ? 'left' : 'right'}: -10px;
    margin-${position === 'start' ? 'right' : 'left'}: 6px;

    .${svgIconClasses.root} {
      margin: 0
    }
  }
  `};
`;

type IAdornment =
  | FunctionComponentElement<SvgIconProps>
  | FunctionComponentElement<IIconButtonProps>;

const Adornment: FC<{
  source: IAdornment;
  position: 'start' | 'end';
  disabled?: boolean;
}> = ({ source: Source, position, disabled }) => (
  <AdornmentContainer position={position}>
    <InputAdornment
      position={position}
      disablePointerEvents={disabled}
      disableTypography
    >
      {(() => Source)()}
    </InputAdornment>
  </AdornmentContainer>
);

export interface ITextFieldProps
  extends Omit<MuiTextFieldProps, 'variant' | 'error' | 'size'> {
  leadingAdornment?: IAdornment;
  trailingAdornment?: IAdornment;
  error?: ReactNode;
  fullWidth?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, ITextFieldProps>(
  (
    {
      error,
      helperText,
      leadingAdornment,
      trailingAdornment,
      multiline,
      ...baseProps
    },

    ref,
  ) => (
    <StyledTextField
      {...baseProps}
      ref={ref}
      variant="outlined"
      error={Boolean(error)}
      helperText={error || helperText}
      multiline={multiline}
      InputLabelProps={{
        shrink: true,
      }}
      InputProps={{
        startAdornment: leadingAdornment && (
          <Adornment
            source={leadingAdornment}
            position="start"
            disabled={baseProps.disabled}
          />
        ),
        endAdornment: trailingAdornment && (
          <Adornment
            source={trailingAdornment}
            position="end"
            disabled={baseProps.disabled}
          />
        ),
      }}
      sx={{
        input: {
          '&::placeholder': {
            opacity: 1,
          },
        },
      }}
    />
  ),
);
